import { annotateFacetExclusions, getUserExcludedFacets } from './searchExclusions';
import {
    DEFAULT_ARCHIVE_STATUSES,
    DEFAULT_LIVE_STATUSES,
    LOAD_CATEGORY_RESULTS_SUCCESS,
    LOAD_SEARCH_RESULTS_SUCCESS,
} from './actions';
import api from '@/redux/api/search';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import uniqBy from 'lodash/uniqBy';

export const LOAD_FACETS_FAIL = 'LOAD_FACETS_FAIL';
export const LOAD_FACETS_REQUEST = 'LOAD_FACETS_REQUEST';
export const LOAD_UPCOMING_FACETS_SUCCESS = 'LOAD_UPCOMING_FACETS_SUCCESS';
const LOAD_ARCHIVED_FACETS_SUCCESS = 'LOAD_ARCHIVED_FACETS_SUCCESS';

// reducer
const DEFAULT_STATE = {
    error: false,
    facets: {
        archived: [],
        upcoming: [],
    },
    submitted: false,
    success: false,
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_FACETS_REQUEST:
            return {
                ...state,
                error: false,
                submitted: true,
                success: false,
            };
        case LOAD_UPCOMING_FACETS_SUCCESS:
            return {
                ...state,
                facets: {
                    ...state.facets,
                    upcoming: action.payload.facets,
                },
                submitted: false,
                success: true,
            };
        case LOAD_CATEGORY_RESULTS_SUCCESS:
        case LOAD_SEARCH_RESULTS_SUCCESS:
            if (action.payload.soldItemFacets?.length) {
                return {
                    ...state,
                    facets: {
                        ...state.facets,
                        archived: action.payload.soldItemFacets,
                    },
                };
            }

            return {
                ...state,
            };
        case LOAD_ARCHIVED_FACETS_SUCCESS:
            return {
                ...state,
                facets: {
                    ...state.facets,
                    archived: action.payload.facets,
                },
                submitted: false,
                success: true,
            };
        case LOAD_FACETS_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.searchFacets;
const idSelector = (state: GlobalState, id) => id;
const searchFacetsSelector = createSelector(stateSelector, (state: State) => state.facets);

export const getFetchFacetsSubmitted = (state: GlobalState) => state.searchFacets.submitted;

export const getRawUpcomingSearchFacets = createSelector(
    searchFacetsSelector,
    getUserExcludedFacets,
    (facets, exclusions) => annotateFacetExclusions(facets.upcoming, exclusions)
);

export const getRawArchivedSearchFacets = createSelector(
    searchFacetsSelector,
    getUserExcludedFacets,
    (facets, exclusions) => annotateFacetExclusions(facets.archived, exclusions)
);

// export const getRawSearchFacets: (state: GlobalState, archivedSearch?: boolean) => any = createSelector(
//     [idSelector, getRawUpcomingSearchFacets, getRawArchivedSearchFacets],
//     (archivedSearch: boolean, rawUpcomingSearchFacets: any, rawArchivedSearchFacets: any) => {
//         archivedSearch ? rawArchivedSearchFacets : rawUpcomingSearchFacets;
//     }
// );

export const getArchivedAndUpcomingFacetValues: (state: GlobalState, facetId?: string) => any = createSelector(
    [idSelector, getRawUpcomingSearchFacets, getRawArchivedSearchFacets],
    (facetId: any, rawUpcomingSearchFacets: any, rawArchivedSearchFacets: any) => {
        const auctionHouseFilter = (facet) => {
            return facet.id === facetId;
        };
        let facets = [];
        let doneFacets = [];

        if (rawUpcomingSearchFacets.length) {
            const filtered = rawUpcomingSearchFacets.filter(auctionHouseFilter);
            if (filtered.length) {
                facets = rawUpcomingSearchFacets.filter(auctionHouseFilter)[0].options;
            }
        }
        if (rawArchivedSearchFacets.length) {
            const filtered = rawArchivedSearchFacets.filter(auctionHouseFilter);
            if (filtered.length) {
                doneFacets = rawArchivedSearchFacets.filter(auctionHouseFilter)[0].options;
            }
        }

        const uniq = uniqBy([...facets, ...doneFacets], (facet) => facet.id);
        return uniq.length ? cloneDeep(uniq) : [];
    }
);

export function getSearchAuctionHouseFacetValues(state: GlobalState) {
    return getArchivedAndUpcomingFacetValues(state, 'auctionHouse');
}

export function getSearchCountryFacetValues(state: GlobalState) {
    return getArchivedAndUpcomingFacetValues(state, 'countryCode');
}

const shouldFetchUpcomingFacets: (state: GlobalState) => any = createSelector(
    [stateSelector],
    (searchFacets: any) =>
        searchFacets.error ||
        (searchFacets.facets && searchFacets.facets.upcoming && searchFacets.facets.upcoming.length === 0)
);

const shouldFetchArchivedFacets: (state: GlobalState) => any = createSelector(
    [stateSelector],
    (searchFacets: any) =>
        searchFacets.error ||
        (searchFacets.facets && searchFacets.facets.archived && searchFacets.facets.archived.length === 0)
);

/* ACTION CREATORS */
const loadFacets = (archivedSearch) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);

        dispatch({
            type: LOAD_FACETS_REQUEST,
        });
        const searchStatus = archivedSearch ? DEFAULT_ARCHIVE_STATUSES : DEFAULT_LIVE_STATUSES;
        const searchQuery = {
            options: {
                status: searchStatus,
            },
        };

        const results = await api.fetchSearchFacetsResults({ authToken, deployment, searchQuery });
        if (results.error) {
            dispatch({
                error: true,
                payload: results,
                type: LOAD_FACETS_FAIL,
            });
        } else {
            const successResult = archivedSearch
                ? {
                      payload: results.payload,
                      type: LOAD_ARCHIVED_FACETS_SUCCESS,
                  }
                : {
                      payload: results.payload,
                      type: LOAD_UPCOMING_FACETS_SUCCESS,
                  };
            dispatch(successResult);
        }
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_FACETS_FAIL,
        });
    }
};

export const fetchSearchFacetsIfNeeded = (archivedSearch: boolean) => async (
    dispatch: Function,
    getState: Function
) => {
    const state = getState();
    const shouldLoadFacets = archivedSearch ? shouldFetchArchivedFacets(state) : shouldFetchUpcomingFacets(state);
    if (shouldLoadFacets) {
        return dispatch(loadFacets(archivedSearch));
    }
    return Promise.resolve();
};
